// app/context/AuthContext.js (Production Ready) ✅
// Copy-Paste

import React, { createContext, useContext, useState, useEffect, useMemo } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import apiService from '../services/ApiService';

const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
};

// ==================== CONFIG (PROD) ====================
const API_BASE_URL = 'https://api.jobeng.ai';

const API_ENDPOINTS = {
  HEALTH: '/health',

  // Auth
  LOGIN: '/api/auth/login',
  REGISTER: '/api/auth/register',
  REGISTER_BUSINESS: '/api/auth/register/business',
  CHECK_USER: '/api/auth/check-user',
  LOGOUT: '/api/auth/logout',
  REFRESH: '/api/auth/refresh',
  VERIFY_EMAIL: '/api/auth/verify-email',

  // Resources
  USERS: '/users',
};

const STORAGE_KEYS = {
  TOKEN: 'auth_token',
  REFRESH: 'refresh_token',
  USER: 'user_data',
};

// ==================== HELPERS ====================
const safeJsonParse = (str) => {
  try {
    return JSON.parse(str);
  } catch {
    return null;
  }
};

const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  // Ensure ApiService baseURL is correct (safe)
  try {
    apiService.baseURL = API_BASE_URL;
  } catch {}

  // ✅ verifyToken using real endpoint /users with explicit Authorization
  const verifyToken = async (token) => {
    if (!token) return false;
    if (token.length < 20 || !token.startsWith('eyJ')) return false;

    const res = await apiService.request(API_ENDPOINTS.USERS, {
      method: 'GET',
      headers: { Authorization: `Bearer ${token}` },
    });

    return res.success === true;
  };

  // ==================== BOOTSTRAP ====================
  useEffect(() => {
    (async () => {
      try {
        const token = await AsyncStorage.getItem(STORAGE_KEYS.TOKEN);
        const userStr = await AsyncStorage.getItem(STORAGE_KEYS.USER);

        if (!token || !userStr) {
          setUser(null);
          setIsAuthenticated(false);
          return;
        }

        const parsedUser = safeJsonParse(userStr);
        if (!parsedUser) {
          await clearAuthData();
          return;
        }

        // Verify token (fail closed)
        const valid = await verifyToken(token);
        if (!valid) {
          await clearAuthData();
          return;
        }

        setUser(parsedUser);
        setIsAuthenticated(true);
      } catch {
        await clearAuthData();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==================== STORAGE ====================
  const clearAuthData = async () => {
    await AsyncStorage.removeItem(STORAGE_KEYS.TOKEN);
    await AsyncStorage.removeItem(STORAGE_KEYS.REFRESH);
    await AsyncStorage.removeItem(STORAGE_KEYS.USER);
    await AsyncStorage.removeItem('company_profile').catch(() => {});
    await AsyncStorage.removeItem('company_id').catch(() => {});
    await AsyncStorage.removeItem('auth_token').catch(() => {});
    await AsyncStorage.removeItem('campaigns_cache').catch(() => {});
    // Clear company-specific cache on logout
    // Clear ALL company-specific caches on logout
    const cacheKeys = ['company_id','company_profile','company_logo','business_subscription','payroll_employees','payroll_payruns','payroll_payslips','payroll_employee_sync','payroll_records_per_employee','payroll_tax_bands','payroll_ni_bands','payroll_settings','payroll_cost_cache','payroll_monthly_cache','hr_employees','hired_employees','erp_settings_v2','erp_favorites','erp_dark_mode','erp_recent_searches','auth_token','campaigns_cache'];
    await AsyncStorage.multiRemove(cacheKeys).catch(() => {});
    try { const allK = await AsyncStorage.getAllKeys(); const scoped = allK.filter(k => k.startsWith('mgmt_emp_cache_') || k.startsWith('mgmt_dept_cache_') || k.startsWith('fin_') || k.startsWith('erp_system_cache_') || k.startsWith('crm_cache_') || k.startsWith('ml_screen_cache_') || k.startsWith('scaleup_cache_') || k.startsWith('epr_dashboard_') || k.startsWith('epr_products_') || k.startsWith('epr_customers_') || k.startsWith('epr_opportunities_') || k.startsWith('innovations_cache_') || k.startsWith('docs_v3_') || k.startsWith('payroll_employees_') || k.startsWith('payroll_employee_sync_') || k.startsWith('payroll_payslips_') || k.startsWith('hired_employees_') || k.startsWith('detail_emp_') || k.startsWith('sent_emails_') || k.startsWith('email_templates_') || k.startsWith('contacts_') || k.startsWith('blog_autosave_')); if (scoped.length) await AsyncStorage.multiRemove(scoped); } catch {}

    setUser(null);
    setIsAuthenticated(false);
  };

  // ==================== LOGIN (PROD) ====================
  const login = async (email, password, userType = 'professional') => {
    try {
      setLoading(true);

      const normalizedEmail = (email || '').toLowerCase().trim();
      if (!normalizedEmail) return { success: false, error: 'Email is required' };
      if (!validateEmail(normalizedEmail)) return { success: false, error: 'Please enter a valid email address' };
      if (!password || !String(password).trim()) return { success: false, error: 'Password is required' };

      // Helpful for confirming the exact URL used (remove later if desired)
      if (typeof apiService.buildUrl === 'function') {
        console.log('LOGIN FINAL URL =>', apiService.buildUrl(API_ENDPOINTS.LOGIN));
      }

      const payloads = [
        { email: normalizedEmail, password, user_type: userType },
        { email: normalizedEmail, password },
        { username: normalizedEmail, password },
      ];

      let lastError = null;

      for (const payload of payloads) {
        const res = await apiService.post(API_ENDPOINTS.LOGIN, payload);

        if (!res.success) {
          lastError = res;
          continue;
        }

        const responseData = res.data;

        let userData = null;
        let token = null;
        let refreshToken = null;

        if (responseData?.user && (responseData?.token || responseData?.access_token)) {
          userData = responseData.user;
          token = responseData.token || responseData.access_token;
          refreshToken = responseData.refresh_token || responseData.refreshToken;
        } else if (responseData?.data?.user && (responseData?.data?.token || responseData?.data?.access_token)) {
          userData = responseData.data.user;
          token = responseData.data.token || responseData.data.access_token;
          refreshToken = responseData.data.refresh_token || responseData.data.refreshToken;
        } else if (responseData?.email && (responseData?.token || responseData?.access_token)) {
          userData = responseData;
          token = responseData.token || responseData.access_token;
          refreshToken = responseData.refresh_token || responseData.refreshToken;
        } else {
          lastError = { success: false, status: res.status, error: 'Invalid response format from server', data: responseData };
          continue;
        }

        if (!userData?.email || !token) {
          lastError = { success: false, status: res.status, error: 'Invalid response format from server', data: responseData };
          continue;
        }

        await AsyncStorage.setItem(STORAGE_KEYS.TOKEN, token);
        if (refreshToken) await AsyncStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);

        const safeUser = {
          ...userData,
          user_type: userData.user_type || userType,
          full_name: userData.full_name || userData.name || normalizedEmail.split('@')[0],
        };

        await AsyncStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(safeUser));

        setUser(safeUser);
        setIsAuthenticated(true);

        return { success: true, user: safeUser };
      }

      const status = lastError?.status;
      let msg = lastError?.error || 'Invalid email or password. Please try again.';
      if (status === 401) msg = 'Invalid credentials. Please check your email and password.';
      if (status === 404) msg = 'Login endpoint not found (404). Check server routes/proxy.';
      if (status === 500) msg = 'Server error. Please try again later.';

      return { success: false, error: msg, debug: lastError };
    } catch {
      return { success: false, error: 'Login failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  // ==================== REGISTER (use ApiService) ====================
  const register = async (newUserData) => {
    try {
      setLoading(true);

      const email = (newUserData?.email || '').toLowerCase().trim();
      if (!validateEmail(email)) return { success: false, error: 'Please enter a valid email address' };
      if (!newUserData?.password) return { success: false, error: 'Password is required' };
      if (!newUserData?.full_name || newUserData.full_name.trim().length < 2) {
        return { success: false, error: 'Please enter your full name' };
      }

      const check = await apiService.post(API_ENDPOINTS.CHECK_USER, { email });
      if (check.success && check.data?.exists) {
        return { success: false, error: 'An account with this email already exists. Please sign in instead.' };
      }

      const registrationData = {
        ...newUserData,
        email,
        user_type: newUserData.user_type || 'professional',
      };

      const endpoint =
        registrationData.user_type === 'business'
          ? API_ENDPOINTS.REGISTER_BUSINESS
          : API_ENDPOINTS.REGISTER;

      const res = await apiService.post(endpoint, registrationData);
      if (!res.success) return { success: false, error: res.error || 'Registration failed. Please try again.' };

      const d = res.data;
      const createdUser = d?.user || d?.data?.user || d?.userData || null;
      const token = d?.token || d?.access_token || d?.data?.token || d?.data?.access_token || null;
      const refreshToken = d?.refresh_token || d?.refreshToken || d?.data?.refresh_token || d?.data?.refreshToken || null;

      if (!createdUser?.email || !token) return { success: false, error: 'Registration failed. Please try again.' };

      await AsyncStorage.setItem(STORAGE_KEYS.TOKEN, token);
      if (refreshToken) await AsyncStorage.setItem(STORAGE_KEYS.REFRESH, refreshToken);
      await AsyncStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(createdUser));

      setUser(createdUser);
      setIsAuthenticated(true);

      return { success: true, user: createdUser };
    } catch {
      return { success: false, error: 'Registration failed. Please try again.' };
    } finally {
      setLoading(false);
    }
  };

  const registerBusiness = async (businessData) =>
    register({
      ...businessData,
      user_type: 'business',
      business_role: businessData?.business_role || 'admin',
    });

  // ==================== LOGOUT (use ApiService) ====================
  const logout = async () => {
    try {
      setLoading(true);

      const token = await AsyncStorage.getItem(STORAGE_KEYS.TOKEN);
      if (token) {
        await apiService.post(API_ENDPOINTS.LOGOUT, {}); // best-effort
      }

      await clearAuthData();
      return { success: true };
    } catch {
      await clearAuthData();
      return { success: true };
    } finally {
      setLoading(false);
    }
  };

  // ==================== REFRESH TOKEN (use ApiService) ====================
  const refreshToken = async () => {
    try {
      const rt = await AsyncStorage.getItem(STORAGE_KEYS.REFRESH);
      if (!rt) return { success: false, error: 'No refresh token available' };

      const res = await apiService.post(API_ENDPOINTS.REFRESH, { refresh_token: rt });

      if (!res.success) {
        await clearAuthData();
        return { success: false, error: 'Session expired' };
      }

      const token = res.data?.token || res.data?.access_token;
      const newRt = res.data?.refresh_token || res.data?.refreshToken;

      if (!token) {
        await clearAuthData();
        return { success: false, error: 'Session expired' };
      }

      await AsyncStorage.setItem(STORAGE_KEYS.TOKEN, token);
      if (newRt) await AsyncStorage.setItem(STORAGE_KEYS.REFRESH, newRt);

      return { success: true, token };
    } catch {
      await clearAuthData();
      return { success: false, error: 'Session expired' };
    }
  };

  // ==================== VALUE ====================
  const value = useMemo(
    () => ({
      user,
      loading,
      isAuthenticated,

      login,
      register,
      registerBusiness,
      logout,
      refreshToken,

      API_BASE_URL,
      API_ENDPOINTS,
    }),
    [user, loading, isAuthenticated]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
